import { parse } from 'yaml';
import type { Locale } from './localeCodes';
import type { LocaleYamlDoc, QuestLocaleBundle, Translations, WildStoryBundle } from './types';

/** Parsed `en.yml` / `ru.yml` without bundled quest/wild_story YAML. */
type MainLocaleYamlDoc = Omit<LocaleYamlDoc, 'quests' | 'wild_story'>;

import enRaw from './en.yml?raw';
import questsEnRaw from './quests.en.yml?raw';
import wildStoryEnRaw from './wild_story.en.yml?raw';

/** Matches backend townVisitLineSlugs (legacy town_visit.type.N → Nth slug). */
export const TOWN_VISIT_SLUG_ORDER: Record<string, readonly string[]> = {
  merchant: [
    'crates_in_shade',
    'practiced_tired_smile',
    'chalk_prices_twice',
    'rumors_bandits_carts',
    'bell_traveler_pack',
    'step_back_tally_gold',
  ],
  healer: [
    'linens_herbs_tent',
    'professional_frown_onceover',
    'slept_badly_nod',
    'tonic_steams_table',
    'blessings_salves_bandages',
    'lighter_under_canvas',
  ],
  quest_giver: [
    'scrolls_wax_desk',
    'ink_stained_map_tap',
    'busy_roads_noncommittal',
    'draft_parchment_smell',
    'squint_spine_legend',
    'promise_listen_worth_it',
  ],
  bounty_hunter: [
    'scrolls_wax_desk',
    'ink_stained_map_tap',
    'busy_roads_noncommittal',
    'draft_parchment_smell',
    'squint_spine_legend',
    'promise_listen_worth_it',
  ],
  elder: [
    'scrolls_wax_desk',
    'ink_stained_map_tap',
    'busy_roads_noncommittal',
    'draft_parchment_smell',
    'squint_spine_legend',
    'promise_listen_worth_it',
  ],
  armorer: [
    'crates_in_shade',
    'practiced_tired_smile',
    'chalk_prices_twice',
    'rumors_bandits_carts',
    'bell_traveler_pack',
    'step_back_tally_gold',
  ],
  weapon: [
    'crates_in_shade',
    'practiced_tired_smile',
    'chalk_prices_twice',
    'rumors_bandits_carts',
    'bell_traveler_pack',
    'step_back_tally_gold',
  ],
  jeweler: [
    'crates_in_shade',
    'practiced_tired_smile',
    'chalk_prices_twice',
    'rumors_bandits_carts',
    'bell_traveler_pack',
    'step_back_tally_gold',
  ],
  generic: [
    'town_noise_blanket',
    'grain_prices_argument',
    'dust_sunbeam_time',
    'strap_tighten_pretend',
    'dog_boring_sleeps',
    'breathe_ready_move_on',
  ],
};

/** Mirrors backend WildStory*Slugs in wild_story_phrase_keys.go. */
export const WILD_STORY_SLUG_ORDER: Record<
  string,
  {
    discover: readonly string[];
    action: Record<string, readonly string[]>;
    outcome: Record<string, readonly string[]>;
  }
> = {
  abandoned_village: {
    discover: ['silent_huts', 'wind_through_shutters', 'collapsed_well', 'overgrown_path'],
    action: {
      search: ['peer_into_hut', 'kick_door', 'sift_rubble', 'check_cellar'],
      leave: ['walk_away', 'circle_back', 'leave_huts_behind'],
      timeout: ['hesitate_passes', 'moment_slips'],
    },
    outcome: {
      nothing: ['empty_homes', 'wind_only_answer'],
      combat: ['shadow_lunges', 'ambush_from_ruin'],
      reward_gold: ['coins_under_floor', 'stash_in_cellar'],
      reward_potion: ['healer_cache', 'dusty_vial'],
      reward_gear: ['gear_in_chest', 'forgotten_pack'],
    },
  },
  ruined_temple: {
    discover: ['cracked_altar', 'mossy_statues', 'fallen_pillars', 'incense_ghost'],
    action: {
      pray: ['kneel_altar', 'whisper_offering', 'trace_runes'],
      search: ['pry_stone', 'sift_dust', 'lift_tile'],
      leave: ['back_away_altar', 'leave_ruins'],
      timeout: ['hesitate_passes', 'gods_wait_no_longer'],
    },
    outcome: {
      nothing: ['gods_silent', 'dust_settles'],
      reward_gold: ['offering_bowl', 'hidden_niche'],
      reward_gear: ['relic_unveiled', 'armory_niche'],
      debuff: ['curse_rises', 'altar_darkens'],
    },
  },
  lost_chest: {
    discover: ['half_buried_root', 'rusted_lock', 'sunken_mound', 'claw_marks_lid'],
    action: {
      open: ['pry_lock', 'force_lid', 'work_hinges'],
      leave: ['walk_past', 'leave_buried'],
      timeout: ['hesitate_passes', 'chest_waits'],
    },
    outcome: {
      nothing: ['empty_inside', 'lock_jammed'],
      combat: ['lid_snaps_ambush', 'trap_springs'],
      reward_gold: ['coins_spill', 'gold_glitter'],
      reward_potion: ['potion_inside', 'glass_clinks'],
      reward_gear: ['gear_revealed', 'wrapped_loot'],
    },
  },
  sanctuary: {
    discover: ['soft_glow_shrine', 'carved_stone_bench', 'flickering_candle', 'woven_offering'],
    action: {
      pray: ['light_candle', 'bow_head', 'touch_stone'],
      rest: ['sit_bench', 'breathe_deep', 'close_eyes'],
      leave: ['quiet_departure', 'leave_shrine'],
      timeout: ['hesitate_passes', 'light_fades'],
    },
    outcome: {
      nothing: ['calm_fades', 'shrine_quiet'],
      buff: ['warm_blessing', 'light_settles'],
    },
  },
  robbed_camp: {
    discover: ['smouldering_coals', 'torn_canvas', 'scattered_bones', 'bootprint_mud'],
    action: {
      search: ['dig_ash', 'check_tent', 'flip_crate'],
      leave: ['avoid_camp', 'move_on_quickly'],
      timeout: ['hesitate_passes', 'embers_cool'],
    },
    outcome: {
      nothing: ['ashes_cold', 'camp_stripped'],
      combat: ['bandit_returns', 'arrow_whistles'],
      reward_gold: ['coin_pouch', 'hidden_stash'],
      reward_gear: ['dropped_pack', 'leftover_gear'],
    },
  },
};

function parseWildStoryBundle(raw: string, label: string): WildStoryBundle {
  const doc = parse(raw) as unknown;
  if (!doc || typeof doc !== 'object') {
    throw new Error(`Invalid ${label} wild_story YAML root`);
  }
  const ws = (doc as { wild_story?: WildStoryBundle }).wild_story;
  if (!ws || typeof ws !== 'object') {
    throw new Error(`${label} wild_story: missing wild_story section`);
  }
  return ws;
}

function validateWildStoryBundle(bundle: WildStoryBundle, label: string): void {
  for (const [family, order] of Object.entries(WILD_STORY_SLUG_ORDER)) {
    const fam = bundle[family];
    if (!fam || typeof fam !== 'object') {
      throw new Error(`${label} wild_story: missing family ${family}`);
    }
    for (const slug of order.discover) {
      const line = fam.discover?.[slug];
      if (typeof line !== 'string' || !line.trim()) {
        throw new Error(`${label} wild_story.${family}.discover.${slug} missing`);
      }
    }
    for (const [action, slugs] of Object.entries(order.action)) {
      const pack = fam.action?.[action];
      if (!pack) {
        throw new Error(`${label} wild_story.${family}.action.${action} missing`);
      }
      for (const slug of slugs) {
        if (typeof pack[slug] !== 'string' || !pack[slug]) {
          throw new Error(`${label} wild_story.${family}.action.${action}.${slug} missing`);
        }
      }
    }
    for (const [outcomeKey, slugs] of Object.entries(order.outcome)) {
      const pack = fam.outcome?.[outcomeKey];
      if (!pack) {
        throw new Error(`${label} wild_story.${family}.outcome.${outcomeKey} missing`);
      }
      for (const slug of slugs) {
        if (typeof pack[slug] !== 'string' || !pack[slug]) {
          throw new Error(`${label} wild_story.${family}.outcome.${outcomeKey}.${slug} missing`);
        }
      }
    }
  }
}

function flattenWildStoryKeys(bundle: WildStoryBundle): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [family, fam] of Object.entries(bundle)) {
    for (const [slug, line] of Object.entries(fam.discover ?? {})) {
      out[`${family}.discover.${slug}`] = line;
    }
    for (const [action, slugs] of Object.entries(fam.action ?? {})) {
      for (const [slug, line] of Object.entries(slugs)) {
        out[`${family}.action.${action}.${slug}`] = line;
      }
    }
    for (const [outcomeKey, slugs] of Object.entries(fam.outcome ?? {})) {
      for (const [slug, line] of Object.entries(slugs)) {
        out[`${family}.outcome.${outcomeKey}.${slug}`] = line;
      }
    }
  }
  return out;
}

function parseQuestBundle(raw: string, label: string): QuestLocaleBundle {
  const doc = parse(raw) as unknown;
  if (!doc || typeof doc !== 'object') {
    throw new Error(`Invalid ${label} quest YAML root`);
  }
  const out: QuestLocaleBundle = {};
  for (const [k, v] of Object.entries(doc as Record<string, unknown>)) {
    if (!v || typeof v !== 'object') {
      throw new Error(`${label} quests.${k}: expected title/description map`);
    }
    const o = v as Record<string, unknown>;
    const title = typeof o.title === 'string' ? o.title.trim() : '';
    const description = typeof o.description === 'string' ? o.description.trim() : '';
    if (!title || !description) {
      throw new Error(`${label} quests.${k}: missing title or description`);
    }
    out[k] = { title, description };
  }
  return out;
}

function assertQuestBundlesMatch(enQ: QuestLocaleBundle, ruQ: QuestLocaleBundle): void {
  const ek = Object.keys(enQ).sort();
  const rk = Object.keys(ruQ).sort();
  if (ek.length !== rk.length) {
    throw new Error(`quests: key count mismatch en=${ek.length} ru=${rk.length}`);
  }
  for (let i = 0; i < ek.length; i++) {
    if (ek[i] !== rk[i]) {
      throw new Error(`quests: sorted keys differ at ${i}: en=${ek[i]} ru=${rk[i]}`);
    }
  }
}

function assertRecordKeysMatch(name: string, a: Record<string, string>, b: Record<string, string>): void {
  const ak = Object.keys(a).sort();
  const bk = Object.keys(b).sort();
  if (ak.length !== bk.length) {
    throw new Error(`${name}: key count mismatch en=${ak.length} ru=${bk.length}`);
  }
  for (let i = 0; i < ak.length; i++) {
    if (ak[i] !== bk[i]) {
      throw new Error(`${name}: sorted keys differ at ${i}: en=${ak[i]} ru=${bk[i]}`);
    }
  }
}

function loadDoc(raw: string): MainLocaleYamlDoc {
  const doc = parse(raw) as unknown;
  if (!doc || typeof doc !== 'object') {
    throw new Error('Invalid locale YAML root');
  }
  const d = doc as Record<string, unknown>;
  const ui = d.ui as Translations | undefined;
  const adventure_log = d.adventure_log as Record<string, string> | undefined;
  const achievements = d.achievements as Record<string, string> | undefined;
  const roadside = d.roadside as Record<string, string> | undefined;
  const town_npc_visit = d.town_npc_visit as Record<string, Record<string, string>> | undefined;
  const enemy_types = d.enemy_types as Record<string, string> | undefined;
  if (!ui || !adventure_log || !achievements || !roadside || !town_npc_visit || !enemy_types) {
    throw new Error(
      'Locale YAML missing required sections (ui, adventure_log, achievements, roadside, town_npc_visit, enemy_types)',
    );
  }
  for (const [k, v] of Object.entries(enemy_types)) {
    if (typeof v !== 'string' || !v.trim()) {
      throw new Error(`Locale YAML empty enemy_types.${k}`);
    }
  }
  for (const [k, v] of Object.entries(roadside)) {
    if (typeof v !== 'string' || !v.trim()) {
      throw new Error(`Locale YAML empty roadside.${k}`);
    }
  }
  for (const npcType of Object.keys(TOWN_VISIT_SLUG_ORDER)) {
    const pack = town_npc_visit[npcType];
    if (!pack || typeof pack !== 'object') {
      throw new Error(`Locale YAML missing town_npc_visit.${npcType}`);
    }
    for (const slug of TOWN_VISIT_SLUG_ORDER[npcType]!) {
      if (typeof pack[slug] !== 'string' || !pack[slug]) {
        throw new Error(`Locale YAML missing town_npc_visit.${npcType}.${slug}`);
      }
    }
  }
  return { ui, adventure_log, achievements, roadside, town_npc_visit, enemy_types } satisfies Omit<
    LocaleYamlDoc,
    'quests' | 'wild_story'
  >;
}

const questsEn = parseQuestBundle(questsEnRaw, 'en');

const wildStoryEn = parseWildStoryBundle(wildStoryEnRaw, 'en');
validateWildStoryBundle(wildStoryEn, 'en');

const mainEn = loadDoc(enRaw);
export const enDoc: LocaleYamlDoc = { ...mainEn, quests: questsEn, wild_story: wildStoryEn };

export const en: Translations = enDoc.ui;

/** Populated after `loadRuLocaleBundle()`; falls back to English until loaded. */
export let ruDoc: LocaleYamlDoc = enDoc;
export let ru: Translations = en;

let ruLoaded = false;
let ruLoadPromise: Promise<void> | null = null;

/** Lazy-load Russian YAML bundles (separate chunk). */
export function loadRuLocaleBundle(): Promise<void> {
  if (ruLoaded) return Promise.resolve();
  if (ruLoadPromise) return ruLoadPromise;
  ruLoadPromise = (async () => {
    const [ruMod, questsRuMod, wildStoryRuMod] = await Promise.all([
      import('./ru.yml?raw'),
      import('./quests.ru.yml?raw'),
      import('./wild_story.ru.yml?raw'),
    ]);
    const questsRu = parseQuestBundle(questsRuMod.default, 'ru');
    assertQuestBundlesMatch(questsEn, questsRu);
    const wildStoryRu = parseWildStoryBundle(wildStoryRuMod.default, 'ru');
    validateWildStoryBundle(wildStoryRu, 'ru');
    assertRecordKeysMatch('wild_story', flattenWildStoryKeys(wildStoryEn), flattenWildStoryKeys(wildStoryRu));
    const mainRu = loadDoc(ruMod.default);
    ruDoc = { ...mainRu, quests: questsRu, wild_story: wildStoryRu };
    assertRecordKeysMatch('enemy_types', enDoc.enemy_types, ruDoc.enemy_types);
    assertRecordKeysMatch('roadside', enDoc.roadside, ruDoc.roadside);
    ru = ruDoc.ui;
    ruLoaded = true;
  })();
  return ruLoadPromise;
}

export function adventureLogTemplate(locale: Locale, code: string): string | undefined {
  const map = locale === 'ru' ? ruDoc.adventure_log : enDoc.adventure_log;
  return map[code];
}

export function achievementLogTitle(locale: Locale, achievementId: string): string {
  if (!achievementId) return '';
  const m = locale === 'ru' ? ruDoc.achievements : enDoc.achievements;
  return m[achievementId] ?? '';
}

/** quest_key from DB → title or description; falls back to server string. */
export function localizedQuestText(
  locale: Locale,
  questKey: string | undefined,
  part: 'title' | 'description',
  fallback: string,
): string {
  if (!questKey) return fallback;
  const bundle = locale === 'ru' ? ruDoc.quests : enDoc.quests;
  const row = bundle[questKey];
  if (!row) return fallback;
  const s = part === 'title' ? row.title : row.description;
  return s?.trim() ? s : fallback;
}

/** Localized display name for DB `enemies.type` slug; empty if unknown. */
export function enemyTypeLabel(locale: Locale, enemyTypeSlug: string): string {
  const slug = enemyTypeSlug?.trim() ?? '';
  if (!slug) return '';
  const map = locale === 'ru' ? ruDoc.enemy_types : enDoc.enemy_types;
  return map[slug] ?? '';
}

/** Phrase code `roadside.<slug>` → localized line. */
export function roadsidePhraseText(locale: Locale, phraseCode: string): string {
  if (!phraseCode.startsWith('roadside.')) return '';
  const slug = phraseCode.slice('roadside.'.length);
  if (!slug || /^\d+$/.test(slug)) return '';
  const map = locale === 'ru' ? ruDoc.roadside : enDoc.roadside;
  return map[slug] ?? '';
}

/** Full phrase code `town_visit.<type>.<slug>` or legacy `town_visit.<type>.<lineIdx>`. */
export function townNpcVisitPhraseText(locale: Locale, phraseCode: string): string {
  if (!phraseCode.startsWith('town_visit.')) return '';
  const rest = phraseCode.slice('town_visit.'.length);
  const lastDot = rest.lastIndexOf('.');
  if (lastDot < 0) return '';
  const codeNpcType = rest.slice(0, lastDot);
  const tail = rest.slice(lastDot + 1);
  const packAll = locale === 'ru' ? ruDoc.town_npc_visit : enDoc.town_npc_visit;
  const pack = packAll[codeNpcType] ?? packAll.generic;
  if (!pack) return '';
  const line = parseInt(tail, 10);
  if (Number.isFinite(line) && String(line) === tail) {
    const slugOrderKey = packAll[codeNpcType] != null ? codeNpcType : 'generic';
    const byType = TOWN_VISIT_SLUG_ORDER as Record<string, readonly string[] | undefined>;
    const picked = byType[slugOrderKey];
    const order: readonly string[] =
      picked !== undefined ? picked : (TOWN_VISIT_SLUG_ORDER as { generic: readonly string[] }).generic;
    const i = Math.max(0, Math.min(order.length - 1, line));
    const slug = order[i] as string;
    return pack[slug] ?? '';
  }
  return pack[tail] ?? '';
}

/** Phrase code `wild_story.<family>.<stage>.…` → localized line. */
export function wildStoryPhraseText(locale: Locale, phraseCode: string): string {
  if (!phraseCode.startsWith('wild_story.')) return '';
  const rest = phraseCode.slice('wild_story.'.length);
  const parts = rest.split('.');
  if (parts.length < 3) return '';
  const family = parts[0]!;
  const stage = parts[1]!;
  const map = locale === 'ru' ? ruDoc.wild_story : enDoc.wild_story;
  const fam = map[family];
  if (!fam) return '';
  if (stage === 'discover' && parts.length === 3) {
    return fam.discover?.[parts[2]!] ?? '';
  }
  if (stage === 'action' && parts.length === 4) {
    return fam.action?.[parts[2]!]?.[parts[3]!] ?? '';
  }
  if (stage === 'outcome' && parts.length === 4) {
    return fam.outcome?.[parts[2]!]?.[parts[3]!] ?? '';
  }
  return '';
}
